import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import OpeningHours from './EstablishmentOpeningHours';
import VideoPlayer from './VideoPlayer';
import EstablishmentContactButton from './EstablishmentContactButton';
import { usePlatformContext } from '../../shared/context/PlatformContext';

function EstablishmentIntro(props) {
  const { establishment, workplacePageUrl, contactPageUrl, autoschadeRumblePageUrl } = props;

  // eslint-disable-next-line camelcase
  const { country } = usePlatformContext();

  const [isLtDesk, setIsLtDesk] = useState(false);

  const [showOpeningHours, setShowOpeningHours] = useState();
  const [openingHoursShowroom, setOpeningHoursShowroom] = useState();
  const [openingHoursWorkplace, setOpeningHoursWorkplace] = useState();
  const [hasOpeningHours, setHasOpeningHours] = useState();

  // DEFAULTS
  useEffect(() => {
    setIsLtDesk((window.innerWidth < 1025));

    window.addEventListener('resize', () => {
      setIsLtDesk((window.innerWidth < 1025));
    });
  }, []);

  useEffect(() => {
    let filled = false;

    const values = Object.values(establishment.openingHours.showroom);
    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      if (value !== null && value !== '') {
        filled = true;
      }
    }

    if (filled) {
      setOpeningHoursShowroom(establishment.openingHours.showroom);
      setHasOpeningHours(true);
    }
  }, [establishment]);

  useEffect(() => {
    let filled = false;

    const values = Object.values(establishment.openingHours.workplace);
    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      if (value !== null && value !== '') {
        filled = true;
      }
    }

    if (filled && !establishment.showStandardOpeningHours) {
      setOpeningHoursWorkplace(establishment.openingHours.workplace);
      setHasOpeningHours(true);
    }
  }, [establishment]);

  useEffect(() => {
    if (!isLtDesk) {
      if (openingHoursShowroom || openingHoursWorkplace) {
        setShowOpeningHours(true);
      }
    }

    if (isLtDesk) {
      setShowOpeningHours(false);
    }
  }, [isLtDesk, openingHoursShowroom, openingHoursWorkplace]);

  let review = null;
  if (establishment.totalRatingPercentage) {
    review = (
      <>
        <div className="viewpoint-reviews">
          <div className="viewpoint-reviews__rating">
            <div className="viewpoint-reviews__stars" style={{ width: `${establishment.totalRatingPercentage}%` }}>★★★★★</div>
            <div className="viewpoint-reviews__stars viewpoint-reviews__stars--inactive">★★★★★</div>
          </div>
          <div className="viewpoint-reviews__score">{`${establishment.totalRating} (${establishment.totalCount})`}</div>
          <a href={establishment.reviewLink} className="viewpoint-reviews__link" target="_blank" rel="noreferrer">Bekijk reviews</a>
        </div>
        <img src="/_resources/themes/schellevis/img/extern-logos/logo-customerviewpoint.png" alt="Customer Viewpoint" width="201" height="21" />
      </>
    );
  }

  return (
    <section className="layer">
      <div className="image-content sign-inv">
        <div className="site-width">
          <div className="layout layout--fit layout--gutter layout--middle">
            <div className="layout__cell unit-6-12--gt-palm unit-1-1 sign">
              {establishment.vimeoLink ? (
                <div className="image-content__visual visual visual--fill">
                  <VideoPlayer
                    src={establishment.vimeoLink}
                    poster={establishment.image.resize}
                    autoplay
                    muted
                    loop
                    controls="custom"
                  />
                </div>
              ) : (
                (establishment.image['1000x750'] && establishment.image['2000x1500'])
                && (
                  <figure className="image-content__visual visual">
                    <picture className="picture" data-default-src={establishment.image['1000x750']}>
                      <source srcSet={`${establishment.image['1000x750']} 1x, ${establishment.image['2000x1500']} 2x`} />
                      <img className="image" width="1000" height="750" alt={establishment.title} src={establishment.image['2000x1500']} />
                    </picture>
                  </figure>
                )
              )}
            </div>
            <div className="layout__cell unit-5-12--desk unit-6-12--lap push-1-12--desk unit-1-1 sign">
              <h2 className="heading sign-s">{establishment.title}</h2>
              <address className="sign-s">
                {`${establishment.address.street} ${establishment.address.houseNumber}`}
                <br />
                {`${establishment.address.postalCode} ${establishment.address.city}`}
              </address>
              <div className="button-group sign-s">
                {establishment.adCallsTelLink
                  && establishment.adCallsEAN
                  && establishment.adCallsTelView
                  ? (
                    <a href={`tel:${establishment.adCallsTelLink}`} className="button-icon button-icon--subtle calltracker-fixed" data-aen={establishment.adCallsEAN}>
                      <div className="button-icon__circle">
                        <svg role="img" className="icon">
                          <use xlinkHref="/_resources/themes/schellevis/img/sprite.svg#phone" />
                        </svg>
                      </div>
                      <span className="button-icon__label calltracker" data-aen={establishment.adCallsEAN}>{establishment.adCallsTelView}</span>
                    </a>
                  ) : establishment.phoneNumberLink && (
                    <a href={`tel:${establishment.phoneNumberLink}`} className="button-icon button-icon--subtle">
                      <div className="button-icon__circle">
                        <svg role="img" className="icon">
                          <use xlinkHref="/_resources/themes/schellevis/img/sprite.svg#phone" />
                        </svg>
                      </div>
                      <span className="button-icon__label">{establishment.phoneNumber}</span>
                    </a>
                  )}
                {establishment.whatsappNumber
                  && (
                    <a href={`https://wa.me/${establishment.whatsappNumber}`} className="button-icon button-icon--subtle">
                      <div className="button-icon__circle">
                        <svg role="img" className="icon">
                          <use xlinkHref="/_resources/themes/schellevis/img/sprite.svg#whatsapp" />
                        </svg>
                      </div>
                      <span className="button-icon__label">{establishment.whatsappNumber}</span>
                    </a>
                  )}
                {establishment.email
                  && (
                    <a href={`mailto:${establishment.email}`} className="button-icon button-icon--subtle" title={establishment.email}>
                      <div className="button-icon__circle">
                        <svg role="img" className="icon icon--stroke">
                          <use xlinkHref="/_resources/themes/schellevis/img/sprite.svg#mail" />
                        </svg>
                      </div>
                      <span className="button-icon__label">{ss.i18n._t('EstablishmentApp.MAILTO_LABEL')}</span>
                    </a>
                  )}
              </div>
              <div className="sign-s">
                {establishment.fax
                  && (
                    <>
                      <strong>
                        {ss.i18n._t('EstablishmentApp.FAX_SHORT')}
                        :
                      </strong>
                      {' '}
                      {establishment.fax}
                      <br />
                    </>
                  )}
                {establishment.taxnumber
                  && (
                    <>
                      <strong>
                        {ss.i18n._t('EstablishmentApp.TAXNUMBER_SHORT')}
                        :
                      </strong>
                      {' '}
                      {establishment.taxnumber}
                      <br />
                    </>
                  )}
                {establishment.iban
                  && (
                    <>
                      <strong>
                        {ss.i18n._t('EstablishmentApp.IBAN_SHORT')}
                        :
                      </strong>
                      {' '}
                      {establishment.iban}
                    </>
                  )}
              </div>
              {review}
              {showOpeningHours
                && (
                  <>
                    <div className="sign" />
                    <div className="layout layout--fit layout--gutter">
                      {openingHoursShowroom
                        && (
                          <div className="layout__cell unit-6-12--desk unit-1-1 sign--lt-desk">
                            <OpeningHours items={openingHoursShowroom} title={openingHoursWorkplace ? ss.i18n._t('EstablishmentApp.OPENINGHOURS_SHOWROOM_HEADING') : ss.i18n._t('EstablishmentApp.OPENINGHOURS_HEADING')} />
                            { country === 'nl' && establishment.services.includes('Schadeherstel') && (
                              <>
                                <div className="sign" />
                                <EstablishmentContactButton
                                  establishment={establishment}
                                  baseUrl={autoschadeRumblePageUrl}
                                  isSchadeherstel
                                />
                              </>
                            )}
                            { country === 'nl' && !establishment.services.includes('Schadeherstel') && (
                              <>
                                <div className="sign" />
                                <EstablishmentContactButton
                                  establishment={establishment}
                                  baseUrl={contactPageUrl}
                                  isShowroom
                                />
                              </>
                            )}
                          </div>
                        )}
                      {openingHoursWorkplace
                        && (
                          <div className="layout__cell unit-6-12--desk unit-1-1 sign--lt-desk">
                            <OpeningHours items={openingHoursWorkplace} title={openingHoursShowroom ? ss.i18n._t('EstablishmentApp.OPENINGHOURS_WORKSHOP_HEADING') : ss.i18n._t('EstablishmentApp.OPENINGHOURS_HEADING')} />
                            { country === 'nl' && (
                              <>
                                <div className="sign" />
                                <EstablishmentContactButton
                                  establishment={establishment}
                                  baseUrl={workplacePageUrl}
                                  isWorkplace
                                />
                              </>
                            )}
                          </div>
                        )}
                    </div>
                  </>
                )}
              {isLtDesk && !showOpeningHours && hasOpeningHours
                && (
                  <>
                    <button type="button" className="button-link" onClick={() => setShowOpeningHours(true)}>
                      {`+  ${ss.i18n._t('EstablishmentApp.SHOW_OPENINGHOURS_LABEL')}`}
                    </button>
                  </>
                )}

              {/* Contact Buttons
              <>
                <div className="sign" />
                <EstablishmentContactButton establishment={establishment} />
              </> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

EstablishmentIntro.propTypes = {
  establishment: PropTypes.shape({
    id: PropTypes.number,
    image: PropTypes.object,
    title: PropTypes.string,
    fax: PropTypes.string,
    taxnumber: PropTypes.string,
    iban: PropTypes.string,
    address: PropTypes.object,
    showStandardOpeningHours: PropTypes.number,
    openingHours: PropTypes.object,
    phoneNumber: PropTypes.string,
    phoneNumberLink: PropTypes.string,
    whatsappNumber: PropTypes.string,
    email: PropTypes.string,
    adCallsTelLink: PropTypes.string,
    adCallsEAN: PropTypes.string,
    adCallsTelView: PropTypes.string,
    totalRatingPercentage: PropTypes.string,
    totalRating: PropTypes.string,
    totalCount: PropTypes.string,
    reviewLink: PropTypes.string,
    vimeoLink: PropTypes.string,
  }).isRequired,
  workplacePageUrl: PropTypes.string,
  contactPageUrl: PropTypes.string,
  autoschadeRumblePageUrl: PropTypes.string,
};

EstablishmentIntro.defaultProps = {
  workplacePageUrl: '/',
  contactPageUrl: '/',
  autoschadeRumblePageUrl: '/',
};

export default EstablishmentIntro;
